/**
 * PATH: packages/ui/src/kanban/components/CardAiBand.tsx
 * FILE_ID: QUI-KANBAN-CARD-AI-BAND-001
 * PURPOSE: The "AI suggested — accept or reject" banner rendered at
 *          the TOP of every Kanban card whose allocation is still
 *          AI_SUGGESTED_PENDING. Mirrors the AI band that lives on
 *          email rows (Module C `<AllocationChip>`) so the operator
 *          gets the same affordance whether they're in the inbox or
 *          on the board.
 *
 * WHY: Module D of EMAIL_KANBAN_ALLOCATION_PLAN.md. User asked:
 *      "when AI allocated to a board and lane, show AI tag and tick
 *      and cross to accept or reject it with clear border so that
 *      we know this was AI tagged".
 *
 *      Distinct visual treatment (amber stripe + AI tag) makes
 *      AI-placed cards instantly scannable next to operator-placed
 *      ones — the user can run their eye down a Triage lane and
 *      tick/cross every AI suggestion without opening any card.
 *
 * FLOW:
 *   render only when allocation_id is non-null
 *     ✓ click → api.confirmAiAllocation()
 *               → tenant onChanged() to refetch lane cards
 *     ✗ click → api.rejectAiAllocation()
 *               → tenant onChanged() to refetch (card removed)
 *
 * DEPENDENCIES:
 *   - ../api : KanbanApi (only the two methods needed; future-proofed
 *              against shape additions)
 *
 * RELATED:
 *   - apps/newmark/src/features/kanban/allocation/AllocationChip.tsx
 *     (sibling — row chip uses the same colours / borders / ✓ ✗)
 *   - components/KanbanCard.tsx (renders this band when applicable)
 */

import { useCallback, useState, type MouseEvent } from 'react'
import { Check, X } from 'lucide-react'

import type { KanbanApi } from '../api'

export interface CardAiBandProps {
  /** Allocation id to confirm / reject. `null` is allowed so the
   *  caller doesn't need to do its own null-check; the band hides
   *  itself in that case. */
  allocationId: string | null
  api: KanbanApi
  /** Fired after a successful confirm / reject so the host can
   *  invalidate cached card lists. */
  onChanged?: () => void
  /** Optional toast bridge. The kanban primitive deliberately doesn't
   *  bake a toaster in — tenants pass their own. */
  notify?: (kind: 'success' | 'error' | 'info', message: string) => void
}

export function CardAiBand({
  allocationId,
  api,
  onChanged,
  notify,
}: CardAiBandProps) {
  const [busy, setBusy] = useState(false)

  const confirm = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation()
      if (!allocationId || busy) return
      setBusy(true)
      try {
        await api.confirmAiAllocation(allocationId)
        notify?.('success', 'AI allocation confirmed.')
        onChanged?.()
      } catch (err) {
        notify?.('error', `Could not confirm: ${(err as Error).message || 'error'}`)
      } finally {
        setBusy(false)
      }
    },
    [allocationId, api, busy, notify, onChanged],
  )

  const reject = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation()
      if (!allocationId || busy) return
      setBusy(true)
      try {
        await api.rejectAiAllocation(allocationId, {
          reason: 'Operator rejected from card AI band',
        })
        notify?.('success', 'AI allocation rejected — card archived.')
        onChanged?.()
      } catch (err) {
        notify?.('error', `Could not reject: ${(err as Error).message || 'error'}`)
      } finally {
        setBusy(false)
      }
    },
    [allocationId, api, busy, notify, onChanged],
  )

  if (!allocationId) return null

  return (
    <div
      // Amber outline with a coloured fill matches the row-chip
      // treatment in Module C. Rounded only on top so the band reads
      // as part of the card head; the card's own border continues
      // below. The whole strip is `pointer-events-auto` even though
      // the parent card is clickable — the buttons stopPropagation
      // so clicking ✓ / ✗ doesn't also open the card drawer.
      role="region"
      aria-label="AI suggested allocation — confirm or reject"
      className="mb-1.5 flex items-center justify-between gap-1 rounded-md border border-amber-400/70 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="inline-flex items-center gap-1 font-semibold">
        <span className="rounded-full bg-amber-500 px-1 py-px text-[8px] leading-none text-white">
          AI
        </span>
        <span>Suggested</span>
      </span>
      <span className="inline-flex items-center gap-0.5">
        <button
          type="button"
          disabled={busy}
          onClick={confirm}
          title="Accept AI suggestion"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-green-700 hover:bg-green-500/20 disabled:opacity-40 dark:text-green-300"
        >
          <Check className="h-3 w-3" />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={reject}
          title="Reject AI suggestion"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-red-700 hover:bg-red-500/20 disabled:opacity-40 dark:text-red-300"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    </div>
  )
}
